// ─── Email verification re-prompt ────────────────────────────────────────────
//
// Onboarding's "Skip for now" writes ft-email-verified = {state:"skipped", at}
// with the note "Do NOT treat 'skipped' as a permanent free pass" — but nothing
// ever read that key back, and no other screen in the app could send or confirm
// a verification code. Skipping was therefore permanent: an account that skipped
// had no route to a verified email short of deleting it and signing up again.
//
// This module is the missing reader. It re-prompts on a widening cadence and
// gives the banner its own verify sheet, reusing the same two endpoints the
// onboarding screen calls.
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { V } from '../utils/theme';
import { LS } from '../utils/storage';
import { apiPost, ApiError } from '../utils/api';
import { Icons } from '../components/Icons';
import { Btn } from '../components/ui';
import { CodeInput, Notice, LinkBtn, IconTile } from '../components/kit';

const KEY = 'ft-email-verified';
const DAY = 86400000;

// Re-ask a day after the skip, then 3 days, then weekly. Deliberately gentle:
// the goal is a recoverable prompt, not a nag that trains people to dismiss.
const BACKOFF = [1 * DAY, 3 * DAY, 7 * DAY];

const readState = () => {
  const raw = LS.get(KEY);
  if (raw === 'true' || raw === true) return { verified: true };
  if (raw && typeof raw === 'object') return { verified: false, at: raw.at, snoozes: raw.snoozes || 0 };
  return { verified: false, at: null, snoozes: 0 };
};

/**
 * Should the banner show right now? Only for an account that skipped (or
 * snoozed) verification and whose backoff window has elapsed.
 */
export const shouldPromptVerify = (email) => {
  if (!email) return false;
  const st = readState();
  if (st.verified) return false;
  if (!st.at) return false; // never went through the skip path — nothing to resume
  const wait = BACKOFF[Math.min(st.snoozes, BACKOFF.length - 1)];
  return Date.now() - new Date(st.at).getTime() >= wait;
};

export const markVerified = () => LS.set(KEY, 'true');

/**
 * Reconcile the local breadcrumb against the server, which is the only
 * authority on email_verified. Needed because accounts created before the
 * skip path recorded a timestamp — and any account restored onto a fresh
 * device — carry no local state at all, so the breadcrumb alone would never
 * prompt them however long they stayed unverified.
 *
 * Verified on the server → stop prompting for good. Unverified with nothing
 * recorded locally → start the backoff clock now, so the first re-prompt lands
 * a day from now rather than the instant the app opens.
 */
export const syncVerifiedFromServer = async () => {
  if (readState().verified) return;
  let json;
  try {
    json = await apiPost('/api/auth/session', { action: 'validate' });
  } catch {
    return; // offline or no session — leave local state untouched
  }
  if (!json?.valid || !json.verification) return;
  if (json.verification.email) { markVerified(); return; }
  const st = readState();
  if (!st.at) LS.set(KEY, { state: 'skipped', at: new Date().toISOString(), snoozes: 0 });
};

const snooze = () => {
  const st = readState();
  LS.set(KEY, { state: 'skipped', at: new Date().toISOString(), snoozes: (st.snoozes || 0) + 1 });
};

// ─── Sheet ───────────────────────────────────────────────────────────────────
const VerifySheet = ({ email, onClose, onVerified }) => {
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const mounted = useRef(true);
  const timers = useRef([]);

  useEffect(() => () => { mounted.current = false; timers.current.forEach(clearInterval); }, []);

  // The code that was mailed at sign-up has long expired by the time this
  // banner appears, so open by sending a fresh one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { send(); }, []);

  const send = async () => {
    try {
      await apiPost('/api/auth/session', { action: 'send_verify_code', email }, { auth: false });
      if (!mounted.current) return;
      setMsg(`Code sent to ${email}`);
    } catch {
      if (mounted.current) setErr('Could not send a code. Check your connection.');
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setErr(''); await send();
    if (!mounted.current) return;
    setCooldown(60);
    const t = setInterval(() => setCooldown(c => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
    timers.current.push(t);
  };

  const confirm = async () => {
    if (code.length !== 6) { setErr('Enter the 6-digit code'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const json = await apiPost('/api/auth/session', { action: 'confirm_verify_code', email, code }, { auth: false });
      if (!mounted.current) return;
      if (json.verified || json.already_verified) { markVerified(); onVerified?.(); onClose(); return; }
      setErr(json.error || 'Incorrect code');
    } catch (e) {
      if (!mounted.current) return;
      setErr(e instanceof ApiError ? (e.body?.error || 'Incorrect code') : 'Connection failed.');
    }
    if (mounted.current) setBusy(false);
  };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(6px)' }} />
      <div role="dialog" aria-modal="true" aria-label="Verify your email" style={{
        position: 'relative', background: V.sheetBg, borderRadius: 22, padding: 24, maxWidth: 380, width: '100%',
        border: `1px solid ${V.cardBorder}`, boxShadow: V.shadowLg,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <IconTile icon={Icons.zap} color={V.accent} size={44} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: V.text, textAlign: 'center' }}>Verify your email</div>
        <div style={{ fontSize: 12, color: V.text3, textAlign: 'center', marginTop: 6, marginBottom: 16, lineHeight: 1.5 }}>
          We sent a 6-digit code to<br /><span style={{ color: V.accent, fontWeight: 700 }}>{email}</span>
          <br />You need this to recover your account if you lose your PIN.
        </div>
        <CodeInput value={code} onChange={v => { setCode(v); setErr(''); }} autoFocus autoComplete="one-time-code"
          onEnter={confirm} state={err ? 'error' : code.length === 6 ? 'ok' : undefined} ariaLabel="Verification code" />
        <Notice tone="error" center style={{ marginBottom: 8 }}>{err}</Notice>
        {!err && <Notice tone="ok" center style={{ marginBottom: 8 }}>{msg}</Notice>}
        <Btn full onClick={confirm} disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify Email'}</Btn>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 14 }}>
          <LinkBtn onClick={resend} disabled={cooldown > 0}>{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}</LinkBtn>
          <span style={{ color: V.text3, fontSize: 12 }}>·</span>
          <LinkBtn color={V.text3} onClick={() => { snooze(); onClose(); }}>Not now</LinkBtn>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Banner + sheet. Renders nothing unless the account actually skipped
 * verification and the backoff window has elapsed.
 */
export const EmailVerifyPrompt = ({ email, TopBanner }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [, bump] = useState(0);

  // One reconcile per app load, so a verification done elsewhere (another
  // device, the onboarding screen) retires this banner promptly.
  useEffect(() => {
    let alive = true;
    syncVerifiedFromServer().then(() => { if (alive) bump(n => n + 1); });
    return () => { alive = false; };
  }, [email]);

  if (dismissed || !shouldPromptVerify(email)) return null;
  return (
    <>
      <TopBanner icon={Icons.zap} color={V.warn} title="Verify your email"
        sub="Without it you can't reset your PIN or recover this account"
        action="Verify" onAction={() => setOpen(true)}
        onDismiss={() => { snooze(); setDismissed(true); }} />
      {open && <VerifySheet email={email} onClose={() => setOpen(false)}
        onVerified={() => setDismissed(true)} />}
    </>
  );
};
